import { useEffect, useRef, useState } from 'react'
import { createDealItem, deleteDealItem, getDealItems, getProducts, getServices, updateDealItem, fmtBRL } from '../../db/api.js'
import DealItemDiscountDialog from './DealItemDiscountDialog.jsx'

const periods = { unico: 'Único', mensal: 'Mensal', trimestral: 'Trimestral', semestral: 'Semestral', anual: 'Anual' }
const parseNumber = (text) => Number(text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text)
export default function DealItemsSummary({ dealId, onValueSaved, onDiscountChange }) {
  const [items, setItems] = useState([])
  const [catalog, setCatalog] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [edit, setEdit] = useState(null)
  const [value, setValue] = useState('')
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [adding, setAdding] = useState(false)
  const [selectedItems, setSelectedItems] = useState([])
  const [discountItem, setDiscountItem] = useState(null)
  useEffect(() => {
    const gross = items.reduce((sum, row) => sum + Number(row.valor_total || 0), 0)
    const net = items.reduce((sum, row) => sum + Number(row.total_com_desconto ?? row.valor_total ?? 0), 0)
    const amount = Math.max(0, Math.round((gross - net) * 100) / 100)
    onDiscountChange?.({ dealId, amount, percent: gross > 0 ? amount / gross * 100 : 0 })
  }, [items, dealId, onDiscountChange])
  const itemTotal = (row) => Number(row.total_com_desconto ?? row.valor_total ?? 0)
  const publishRows = (rows) => {
    setItems(rows)
    onValueSaved?.(rows.reduce((sum, row) => sum + itemTotal(row), 0))
  }
  const applyDiscount = async (percent) => {
    let next
    if (import.meta.env.DEV && import.meta.env.MODE === 'demo') {
      const { saveDemoSummaryItem } = await import('../../demo/data.js')
      next = saveDemoSummaryItem(dealId, discountItem, { desconto_percentual: percent })
    } else {
      const updated = await updateDealItem(dealId, discountItem.id, { desconto_percentual: Math.round(percent * 100) / 100 })
      next = { ...discountItem, ...updated }
    }
    publishRows(items.map((row) => row.id === next.id ? { ...row, ...next } : row))
  }
  const add = async (event) => {
    event.preventDefault()
    if (lock.current) return
    const entries = catalog.filter((row) => selectedItems.includes(`${row.tipo}:${row.id}`))
    if (!entries.length) { setEditError('Selecione ao menos um produto ou serviço.'); return }
    lock.current = true; setSaving(true); setEditError('')
    try {
      let rows = items
      if (import.meta.env.DEV && import.meta.env.MODE === 'demo') {
        const { addDemoSummaryItem } = await import('../../demo/data.js')
        for (const entry of entries) rows = addDemoSummaryItem(dealId, rows, entry)
        publishRows(rows)
      } else {
        for (const entry of entries) {
          const created = await createDealItem(dealId, {
            productId: entry.tipo === 'produto' ? entry.id : null,
            serviceId: entry.tipo === 'servico' ? entry.id : null,
            descricao: entry.nome,
            quantidade: 1,
            precoUnitario: Number(entry.preco || 0),
            periodicidade: entry.tipo === 'servico' ? entry.periodicidade || 'unico' : null,
          })
          rows = [...rows, { ...created, catalogo_nome: entry.nome, tipo: entry.tipo }]
          publishRows(rows)
        }
      }
      setAdding(false); setSelectedItems([])
    } catch (err) { setEditError(err.message) }
    finally { lock.current = false; setSaving(false) }
  }
  const lock = useRef(false)
  useEffect(() => {
    let alive = true
    setLoading(true); setError(''); setItems([]); setEdit(null)
    getDealItems(dealId).then((rows) => { if (alive) setItems(rows) }).catch(() => { if (alive) setError('Não foi possível carregar os itens.') }).finally(() => { if (alive) setLoading(false) })
    Promise.all([getProducts(), getServices()]).then(([products, services]) => { if (alive) setCatalog([...products.map((item) => ({ ...item, tipo: 'produto' })), ...services.map((item) => ({ ...item, tipo: 'servico' }))]) }).catch(() => { if (alive) setCatalog([]) })
    return () => { alive = false }
  }, [dealId])
  const begin = (item, field) => {
    if (saving) return
    setAdding(false)
    setEdit({ id: item.id, field }); setEditError(''); setSearch('')
    setValue(field === 'catalog' ? '' : field === 'periodicidade' ? item.periodicidade || 'unico' : Number(item[field] ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: field === 'preco_unitario' ? 2 : 0, maximumFractionDigits: 3 }))
  }
  const save = async (event, item) => {
    event.preventDefault()
    if (lock.current) return
    setEditError('')
    let changes
    if (edit.field === 'catalog') {
      const selected = catalog.find((entry) => `${entry.tipo}:${entry.id}` === value)
      if (!selected) { setEditError('Selecione um produto ou serviço.'); return }
      changes = { catalogo_nome: selected.nome, descricao: selected.descricao || '', tipo: selected.tipo, preco_unitario: Number(selected.preco || 0), periodicidade: selected.periodicidade || 'unico', product_id: selected.tipo === 'produto' ? selected.id : null, service_id: selected.tipo === 'servico' ? selected.id : null }
    } else if (edit.field === 'periodicidade') changes = { periodicidade: value }
    else {
      const number = parseNumber(value.trim().replace(/\s/g, ''))
      if (!value.trim() || !Number.isFinite(number) || number < 0 || (edit.field === 'quantidade' && number <= 0)) { setEditError('Informe um valor válido.'); return }
      changes = { [edit.field]: number }
    }
    lock.current = true; setSaving(true)
    try {
      let next
      if (import.meta.env.DEV && import.meta.env.MODE === 'demo') {
        const { saveDemoSummaryItem } = await import('../../demo/data.js')
        next = saveDemoSummaryItem(dealId, item, changes)
      } else if (edit.field === 'catalog') {
        const selected = catalog.find((entry) => `${entry.tipo}:${entry.id}` === value)
        let created = null
        try {
          created = await createDealItem(dealId, {
            productId: selected.tipo === 'produto' ? selected.id : null,
            serviceId: selected.tipo === 'servico' ? selected.id : null,
            descricao: selected.nome,
            quantidade: Number(item.quantidade),
            precoUnitario: Number(selected.preco || 0),
            periodicidade: selected.tipo === 'servico' ? selected.periodicidade || 'unico' : null,
          })
          await deleteDealItem(dealId, item.id)
        } catch (error) {
          if (created?.id) await deleteDealItem(dealId, created.id).catch(() => {})
          throw error
        }
        next = { ...created, catalogo_nome: selected.nome, tipo: selected.tipo }
      } else {
        const updated = await updateDealItem(dealId, item.id, changes)
        next = { ...item, ...updated }
      }
      publishRows(items.map((row) => row.id === item.id ? next : row))
      setEdit(null)
    } catch (err) { setEditError(err.message) }
    finally { lock.current = false; setSaving(false) }
  }
  const actions = (item, field) => edit?.id === item.id && edit.field === field && <>
    <div className="dp-price-actions"><button type="submit" form={`item-${item.id}`} disabled={saving}>{saving ? 'Salvando…' : 'Salvar'}</button><button type="button" disabled={saving} onClick={() => setEdit(null)}>Cancelar</button></div>
    {editError && <p role="alert">{editError}</p>}
  </>
  const field = (item, key, label) => <>
    <div className={`cv-data-row${key === 'quantidade' && !(edit?.id === item.id && edit.field === key) ? ' dp-quantity-clickable' : ''}`}><span className="cv-data-label">{label}</span>
      {edit?.id === item.id && edit.field === key ? <form id={`item-${item.id}`} className="dp-price-inline" onSubmit={(event) => save(event, item)}>
        {key === 'periodicidade' ? <select autoFocus aria-label={label} value={value} disabled={saving} onChange={(event) => setValue(event.target.value)}>{Object.entries(periods).map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select> : <>{key === 'preco_unitario' && <span>R$</span>}<input autoFocus inputMode="decimal" aria-label={label} value={value} readOnly={saving} style={{ width: `${Math.max(value.length, 3)}ch` }} onChange={(event) => setValue(event.target.value)} /></>}
      </form> : <button type="button" className="dp-price-value cv-data-value" onClick={() => begin(item, key)}>{key === 'preco_unitario' ? fmtBRL(item[key] || 0) : key === 'periodicidade' ? periods[item[key] || 'unico'] || item[key] : item[key]}</button>}
    </div>{actions(item, key)}
  </>
  return <section className="dp-items-summary" aria-label="Produtos e serviços do negócio" onKeyDown={(event) => { if (edit && event.key === 'Escape') { event.stopPropagation(); if (!saving) setEdit(null) } }}>
    <div className="cv-row-label">Produtos e serviços</div>
    {loading ? <p>Carregando itens…</p> : error ? <p role="alert">{error}</p> : !items.length ? <p>Nenhum produto ou serviço vinculado.</p> : items.map((item) => <article key={item.id}>
      <div className="dp-item-heading"><button type="button" className="dp-item-name" onClick={() => begin(item, 'catalog')} aria-expanded={edit?.id === item.id && edit.field === 'catalog'}>{item.catalogo_nome || item.descricao || 'Item'}</button><span>{item.tipo === 'servico' ? 'Serviço' : 'Produto'}</span></div>
      {edit?.id === item.id && edit.field === 'catalog' && <form id={`item-${item.id}`} className="dp-item-catalog" onSubmit={(event) => save(event, item)}>
        <input autoFocus aria-label="Buscar produto ou serviço" placeholder="Buscar produto ou serviço…" value={search} onChange={(event) => setSearch(event.target.value)} />
        <div className="dp-item-catalog-options">{catalog.filter((entry) => entry.nome.toLocaleLowerCase().includes(search.toLocaleLowerCase())).map((entry) => <button type="button" key={`${entry.tipo}:${entry.id}`} aria-pressed={value === `${entry.tipo}:${entry.id}`} onClick={() => setValue(`${entry.tipo}:${entry.id}`)}><span className="dp-catalog-identity"><span>{entry.nome}</span><small>{entry.tipo === 'servico' ? 'Serviço' : 'Produto'}</small></span><span className="dp-catalog-price">{fmtBRL(entry.preco || 0)}</span></button>)}{!catalog.length && <p>Catálogo indisponível.</p>}</div>
      </form>}{actions(item, 'catalog')}
      {item.descricao && item.descricao !== item.catalogo_nome && <p className="dp-item-description">{item.descricao}</p>}
      {field(item, 'quantidade', 'Quantidade')}
      {field(item, 'preco_unitario', 'Valor unitário')}
      {field(item, 'periodicidade', 'Periodicidade')}
      <div className="cv-data-row"><span className="cv-data-label">Total do item</span><span className="cv-data-value">{Number(item.desconto_percentual) > 0 ? <s>{fmtBRL(item.valor_total || 0)}</s> : fmtBRL(item.valor_total || 0)}</span></div>
      {Number(item.desconto_percentual) > 0 && <>
        <div className="cv-data-row"><span className="cv-data-label">Desconto</span><span className="cv-data-value">{Number(item.desconto_percentual).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</span></div>
        <div className="cv-data-row"><span className="cv-data-label">Total com desconto</span><span className="cv-data-value">{fmtBRL(itemTotal(item))}</span></div>
      </>}
    </article>)}
    {!loading && !error && <>
      <button type="button" className="dp-add-item" disabled={saving} aria-expanded={adding} onClick={() => { setAdding(!adding); setSelectedItems([]); setEdit(null); setSearch(''); setValue(''); setEditError('') }}>+ Adicionar produto ou serviço</button>
      {adding && <form className="dp-item-catalog" onSubmit={add} onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); if (!saving) setAdding(false) } }}>
        <input autoFocus aria-label="Buscar novo produto ou serviço" placeholder="Buscar produto ou serviço…" value={search} onChange={(event) => setSearch(event.target.value)} />
        <div className="dp-item-catalog-options">{catalog.filter((entry) => entry.nome.toLocaleLowerCase().includes(search.toLocaleLowerCase())).map((entry) => <label className="dp-catalog-check" key={`${entry.tipo}:${entry.id}`}><input type="checkbox" disabled={saving} checked={selectedItems.includes(`${entry.tipo}:${entry.id}`)} onChange={(event) => { const key = `${entry.tipo}:${entry.id}`; setSelectedItems((current) => event.target.checked ? [...current, key] : current.filter((id) => id !== key)) }} /><span className="dp-catalog-identity"><span>{entry.nome}</span><small>{entry.tipo === 'servico' ? 'Serviço' : 'Produto'}</small></span><span className="dp-catalog-price">{fmtBRL(entry.preco || 0)}</span></label>)}{!catalog.filter((entry) => entry.nome.toLocaleLowerCase().includes(search.toLocaleLowerCase())).length && <p>Nenhum item encontrado.</p>}</div>
        {editError && <p role="alert">{editError}</p>}
        <div className="dp-price-actions"><button type="submit" disabled={saving || !selectedItems.length}>{saving ? 'Adicionando…' : selectedItems.length ? `Adicionar (${selectedItems.length})` : 'Adicionar'}</button><button type="button" disabled={saving} onClick={() => setAdding(false)}>Cancelar</button></div>
      </form>}
    </>}
    {discountItem && <DealItemDiscountDialog item={discountItem} onApply={applyDiscount} onClose={() => setDiscountItem(null)} />}
  </section>
}
